import { memo, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { ShortUrlObjectType } from '@air/api/types';
import { type TabPanel, Tabs } from '@air/primitive-tabs';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { NoPermissionToCreateShareLink } from '~/components/NoPermissionToCreateShareLink';
import { EditableShareLink } from '~/components/ShareModal/components/ShareLink/EditableShareLink';
import { ReadonlyShareLink } from '~/components/ShareModal/components/ShareLink/ReadonlyShareLink';
import { ShareLinkModalFooter } from '~/components/ShareModal/components/ShareLinkModalFooter';
import { ShareLinkModal } from '~/components/ShareModal/ShareLinkModal';
import { ShareBoardDesignTab } from '~/components/SharePrivateBoardModal/ShareBoardDesignTab';
import { SharePrivateBoardModalBody } from '~/components/SharePrivateBoardModal/SharePrivateBoardModalBody';
import { SharePrivateBoardRegenerate } from '~/components/SharePrivateBoardModal/SharePrivateBoardRegenerate';
import { SharePrivateContentSpinner } from '~/components/SharePrivateContentModal/SharePrivateContentSpinner';
import { SHARE_PRIVATE_CONTENT_MODAL } from '~/constants/testIDs';
import { useShowTurnOnShareLinkNotifications } from '~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useShowShareLinkTeamInviteNudgeForModal } from '~/hooks/useShowShareLinkTeamInviteNudgeForModal';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetExistingShortIdInformation } from '~/swr-hooks/shortUrl/useGetExistingShortIdInformation';
import { useGetShortIdForBoard } from '~/swr-hooks/shortUrl/useGetShortIdForBoard';
import {
  canChangeAssetCustomFields,
  canEditAssetDiscussions,
  canExportAsset,
  canSeeAssetCustomFields,
  canViewAssetDiscussions,
} from '~/utils/permissions/assetPermissions';
import { canCreateShortUrl, canEditShortUrl, canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';

export type SharePrivateBoardModalProps = AirModalProps<{
  boardId: string;
  trackLocation: AirActionTrackingLocation;
}>;

export const SharePrivateBoardModal = memo(({ onClose, boardId, trackLocation }: SharePrivateBoardModalProps) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [hasCreatedShareLinkInThisSession, setHasCreatedShareLinkInThisSession] = useState(false);
  const { currentWorkspace } = useCurrentWorkspace();
  const { showToast } = useToasts();
  const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
  const { boardPermissions } = useBoardPermissions({
    boardId,
  });
  const canEdit = useMemo(() => canEditShortUrl(boardPermissions), [boardPermissions]);
  const canCreate = useMemo(() => canCreateShortUrl(boardPermissions), [boardPermissions]);
  const canViewCustomFields = useMemo(() => canSeeAssetCustomFields(boardPermissions), [boardPermissions]);
  const canEditCustomFields = useMemo(() => canChangeAssetCustomFields(boardPermissions), [boardPermissions]);
  const canManageDiscussions = useMemo(() => canEditAssetDiscussions(boardPermissions), [boardPermissions]);
  const canViewDiscussions = useMemo(() => canViewAssetDiscussions(boardPermissions), [boardPermissions]);
  const workspaceCanDownload = useMemo(() => canExportAsset(boardPermissions), [boardPermissions]);
  const { data: shortIdInfo, isLoading: isInitialLoading } = useGetExistingShortIdInformation({
    id: boardId,
    type: ShortUrlObjectType.board,
    boardContext: undefined,
    canView: canViewShortUrl(boardPermissions),
  });

  const [showRegenerateModal] = useAirModal(SharePrivateBoardRegenerate);
  const [showShareModal, hideShareModal] = useAirModal(SharePrivateBoardModal);
  const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();

  const {
    mutate: createShortIdForBoard,
    isPending: isCreatingShortIdForBoard,
    isError: isErrorCreatingShortIdForBoard,
  } = useGetShortIdForBoard();

  const hasShortId = !!shortIdInfo;

  const handleShareLinkCreatedFallback = useCallback(() => {
    showTurnOnShareLinkNotifications({
      onManageClick: onClose,
    });
  }, [onClose, showTurnOnShareLinkNotifications]);

  // Kept for its side effect only: when the auto-create effect below mints a share link, this
  // hook's `onShareLinkCreatedFallback` fires the "turn on share link notifications" toast. The
  // team-invite nudge panel it can also drive never shipped on this modal, so nothing is rendered.
  useShowShareLinkTeamInviteNudgeForModal({
    isShareLinkCreated: hasCreatedShareLinkInThisSession,
    isShareLinkReady: hasShortId,
    location: 'board_share_link',
    onShareLinkCreatedFallback: handleShareLinkCreatedFallback,
  });

  useEffect(() => {
    const workspaceId = currentWorkspace?.id;

    if (!workspaceId) {
      return;
    }
    if (
      !isInitialLoading &&
      !hasShortId &&
      canCreate &&
      !isCreatingShortIdForBoard &&
      !isErrorCreatingShortIdForBoard
    ) {
      createShortIdForBoard(
        { workspaceId, id: boardId },
        {
          onSuccess: () => {
            setHasCreatedShareLinkInThisSession(true);
          },
          onError: () => {
            showToast('Something went wrong. Please try again later.', {
              color: 'red',
            });
          },
        },
      );
    }
  }, [
    currentWorkspace?.id,
    boardId,
    canCreate,
    createShortIdForBoard,
    isInitialLoading,
    isCreatingShortIdForBoard,
    isErrorCreatingShortIdForBoard,
    showToast,
    hasShortId,
  ]);

  const handleRegenerate = useCallback(() => {
    if (!shortIdInfo) {
      return;
    }
    hideShareModal();

    const reopenShareModal = () => {
      showShareModal({
        boardId,
        trackLocation,
      });
    };

    showRegenerateModal({
      boardId,
      trackLocation,
      shortIdInfo,
      onRegenerated: () => {
        reopenShareModal();
        showTurnOnShareLinkNotifications({
          onManageClick: hideShareModal,
        });
      },
      onCancel: reopenShareModal,
    });
  }, [
    shortIdInfo,
    hideShareModal,
    showRegenerateModal,
    boardId,
    trackLocation,
    showShareModal,
    showTurnOnShareLinkNotifications,
  ]);

  if (showingSubscriptionExpiredModal) {
    return null;
  }

  let content: ReactNode;

  // A user who can create a link but has none yet is waiting on the auto-create effect above, so
  // they get the spinner — including after a failed attempt, where the effect's red toast is the
  // error presentation. Only a user who cannot create one is told they lack permission.
  if (isInitialLoading || (!shortIdInfo && canCreate)) {
    content = <SharePrivateContentSpinner />;
  } else if (!shortIdInfo) {
    content = <NoPermissionToCreateShareLink />;
  } else if (!canEdit) {
    content = (
      <ReadonlyShareLink
        objectId={boardId}
        objectType="board"
        shareId={shortIdInfo.id}
        shortIdInfo={shortIdInfo}
        trackLocation={trackLocation}
      />
    );
  } else {
    const panels: TabPanel[] = [
      {
        id: 'settings',
        label: 'Settings',
        // The body renders a flat fragment of sections and relies on its container for stack
        // spacing, matching the non-tabbed modal's old `flex flex-col gap-6` children container.
        // The flex wrapper lives INSIDE TabsContent (not via contentClassName) so the inactive
        // panel's `hidden` attribute can collapse it to zero height — a display class on
        // TabsContent would override `[hidden] { display:none }` and leave empty padding behind.
        content: (
          <div className="flex flex-col gap-6">
            <SharePrivateBoardModalBody
              boardId={boardId}
              canEdit={canEdit}
              canEditCustomFields={canEditCustomFields}
              canManageDiscussions={canManageDiscussions}
              canViewCustomFields={canViewCustomFields}
              canViewDiscussions={canViewDiscussions}
              shortIdInfo={shortIdInfo}
              workspaceCanDownload={workspaceCanDownload}
            />
          </div>
        ),
      },
      {
        id: 'design',
        label: 'Design',
        content: <ShareBoardDesignTab boardId={boardId} shortIdInfo={shortIdInfo} />,
      },
    ];

    content = (
      <Tabs
        className="flex min-h-0 flex-1 flex-col"
        data-testid="SHARE_PRIVATE_BOARD_MODAL_TABS"
        listClassName="shrink-0 border-b ads:border-b-default border-b-gray-4 px-6 pt-1"
        onValueChange={setActiveTab}
        panels={panels}
        panelsClassName="min-h-0 overflow-y-auto px-6 pb-4 pt-4"
        value={activeTab}
      />
    );
  }

  const footer =
    shortIdInfo && canEdit ? (
      <EditableShareLink
        createdAt={shortIdInfo.createdAt}
        createdBy={shortIdInfo.createdBy}
        objectId={boardId}
        objectType="board"
        shareId={shortIdInfo.id}
        shortIdInfo={shortIdInfo}
        trackLocation={trackLocation}
        onRegenerate={handleRegenerate}
      />
    ) : undefined;

  // The editable modal renders Tabs, which own their scroll region; the read-only, loading, and
  // no-permission bodies need a plain scrollable wrapper instead.
  const isTabbed = !!(shortIdInfo && canEdit);

  return (
    <ShareLinkModal data-testid={SHARE_PRIVATE_CONTENT_MODAL} title="Share board link" onClose={onClose}>
      {isTabbed ? (
        content
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto border-t ads:border-t-default border-t-gray-4 px-6 pt-4 pb-4">
          {content}
        </div>
      )}
      {footer ? <ShareLinkModalFooter>{footer}</ShareLinkModalFooter> : null}
    </ShareLinkModal>
  );
});

SharePrivateBoardModal.displayName = 'SharePrivateBoardModal';
